const DB_NAME = 'model-thumbnails';
const STORE_NAME = 'thumbnails';
const DB_VERSION = 1;

interface ThumbnailEntry {
  readonly key: string;
  readonly blob: Blob;
  readonly createdAt: number;
}

const openDb = (): Promise<IDBDatabase> =>
  new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);
    request.onupgradeneeded = () => {
      const db = request.result;
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        db.createObjectStore(STORE_NAME, { keyPath: 'key' });
      }
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });

const buildKey = (productId: string, fileKey: string): string =>
  `${productId}:${fileKey}`;

export const thumbnailCache = {
  get: async (productId: string, fileKey: string): Promise<Blob | null> => {
    try {
      const db = await openDb();
      return new Promise((resolve) => {
        const tx = db.transaction(STORE_NAME, 'readonly');
        const store = tx.objectStore(STORE_NAME);
        const request = store.get(buildKey(productId, fileKey));
        request.onsuccess = () => {
          const entry = request.result as ThumbnailEntry | undefined;
          resolve(entry?.blob ?? null);
        };
        request.onerror = () => resolve(null);
      });
    } catch {
      return null;
    }
  },

  put: async (productId: string, fileKey: string, blob: Blob): Promise<void> => {
    try {
      const db = await openDb();
      return new Promise((resolve, reject) => {
        const tx = db.transaction(STORE_NAME, 'readwrite');
        const store = tx.objectStore(STORE_NAME);
        const entry: ThumbnailEntry = {
          key: buildKey(productId, fileKey),
          blob,
          createdAt: Date.now(),
        };
        const request = store.put(entry);
        request.onsuccess = () => resolve();
        request.onerror = () => reject(request.error);
      });
    } catch {
      // Silently fail - thumbnails will regenerate next time
    }
  },
};
